import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';

// Components
import Button from 'reactstrap/lib/Button';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import { YmmsDropdowns } from 'site-modules/shared/components/ymms-dropdowns/ymms-dropdowns';

import './car-select-ymms-modal.scss';

const ARIA_LABEL_TITLE_ID = 'modal-title-car-select';

export function CarSelectYmmsModal({
  toggleModal,
  isModalOpened,
  isEmpty,
  year = null,
  makeSlug = null,
  modelSlug = null,
  id = null,
  name = null,
  submodels = {},
  addVehicle,
}) {
  const closeBtnRef = useRef();

  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  const getButton = useCallback(
    vehicleData => {
      function applyVehicle({
        target: {
          dataset: { styleId, submodel },
        },
      }) {
        addVehicle(styleId, submodel);
        toggleModal();
      }

      const styleId = get(vehicleData, 'style.id', '');
      const submodel = get(vehicleData, 'submodel.niceId', '');
      return (
        <div className="text-center mt-1">
          <Button
            color="outline-blue-50"
            className="size-16 py-0_5 px-1_25"
            disabled={!(vehicleData.make && vehicleData.submodel && vehicleData.year && vehicleData.style)}
            onClick={applyVehicle}
            data-tracking-id="mmy_select_submit_style"
            data-tracking-value={styleId}
            data-style-id={styleId}
            data-submodel={submodel}
          >
            {isEmpty ? 'Add Vehicle' : 'Change Vehicle'}
          </Button>
        </div>
      );
    },
    [addVehicle, isEmpty, toggleModal]
  );

  return (
    <Modal
      className="car-select-modal"
      size="small"
      contentClassName="py-1_25 px-0_75 p-md-2"
      isOpen={isModalOpened}
      toggle={toggleModal}
      labelledBy={ARIA_LABEL_TITLE_ID}
    >
      <ModalHeader
        className="p-0 border-0 text-cool-gray-10"
        cssModule={{ 'modal-title': 'heading-4 mb-0 text-gray-darker' }}
        tag="h2"
      >
        <span id={ARIA_LABEL_TITLE_ID}>{isEmpty ? 'Add Vehicle' : 'Change Vehicle'}</span>
        <Button
          color="link"
          className="modal-close-btn p-0_25 text-gray-darker size-20 btn-bg-white pos-a top-0 right-0 mt-0_5 me-0_5 mt-md-0_75 me-md-0_75"
          onClick={toggleModal}
          aria-label="Close Modal"
          innerRef={closeBtnRef}
        >
          <i className="icon-cross2" aria-hidden />
        </Button>
      </ModalHeader>
      <ModalBody className="pt-0_75 pb-0 px-0 text-cool-gray-30">
        <YmmsDropdowns
          dropdownClassName="mmy-select w-100 my-0_5"
          dropdownPlaceholders={{
            year: 'Select Year',
            make: 'Select Make',
            submodel: 'Select Model',
            style: 'Select Style',
          }}
          dropdownLabels={{
            year: 'Year',
            make: 'Make',
            model: 'Model',
            submodel: 'Model',
            style: 'Style',
          }}
          prepopulatedVehicle={{
            makeSlug,
            year,
            modelSlug,
            id,
            name,
            submodels,
          }}
          useMostPopularStylesDropdown
          useStyledSelects
          labelClassName="d-block size-12 my-0_5"
        >
          {getButton}
        </YmmsDropdowns>
      </ModalBody>
    </Modal>
  );
}

CarSelectYmmsModal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  isModalOpened: PropTypes.bool.isRequired,
  isEmpty: PropTypes.bool.isRequired,
  addVehicle: PropTypes.func.isRequired,
  name: PropTypes.string,
  submodels: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
  makeSlug: PropTypes.string,
  year: PropTypes.number,
  modelSlug: PropTypes.string,
  id: PropTypes.number,
};
